import React, { useState, useEffect, useRef } from "react";
import { getProjects } from "../api/client";

const FILTERS = ["Semua", "Hardware", "Software", "Networking"];

function HoloCard({ project, index }) {
  const cardRef = useRef();
  const shineRef = useRef();
  const [hovered, setHovered] = useState(false);

  const onMove = (e) => {
    const rect = cardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 22;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -22;
    if (cardRef.current) cardRef.current.style.transform = `perspective(900px) rotateX(${y}deg) rotateY(${x}deg) scale3d(1.02,1.02,1.02)`;
    // Shine position
    if (shineRef.current) {
      const px = ((e.clientX - rect.left) / rect.width) * 100;
      const py = ((e.clientY - rect.top) / rect.height) * 100;
      shineRef.current.style.background = `radial-gradient(circle at ${px}% ${py}%, rgba(255,255,255,0.12) 0%, transparent 60%)`;
      shineRef.current.style.opacity = 1;
    }
  };
  const onLeave = () => {
    setHovered(false);
    if (cardRef.current) cardRef.current.style.transform = "perspective(900px) rotateX(0) rotateY(0) scale3d(1,1,1)";
    if (shineRef.current) shineRef.current.style.opacity = 0;
  };

  const catColor = { Hardware: "#ffb700", Software: "#00e5ff", Networking: "#ff0099" }[project.category] || "#00e5ff";

  return (
    <div
      ref={cardRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={onLeave}
      onMouseMove={onMove}
      style={{
        background: "var(--card)",
        borderRadius: 4, overflow: "hidden",
        border: "1px solid",
        borderColor: hovered ? "transparent" : "rgba(255,255,255,0.07)",
        backgroundImage: hovered
          ? `linear-gradient(var(--card),var(--card))`
          : "none",
        outline: hovered ? `1px solid ${catColor}40` : "none",
        transition: "transform 0.15s ease, outline 0.2s",
        cursor: "none",
        position: "relative",
        animation: `fadeUp 0.6s ease ${index * 0.08}s both`,
      }}
    >
      {/* Holographic shine overlay */}
      <div ref={shineRef} style={{ position: "absolute", inset: 0, opacity: 0, pointerEvents: "none", zIndex: 3, transition: "opacity 0.2s", borderRadius: 4 }} />

      {/* Category accent line */}
      <div style={{ height: 2, background: catColor, opacity: hovered ? 1 : 0.3, transition: "opacity 0.2s" }} />

      {/* Image */}
      {project.image && (
        <div style={{ height: 180, overflow: "hidden", position: "relative" }}>
          <img src={project.image} alt={project.title}
            style={{ width: "100%", height: "100%", objectFit: "cover",
              filter: hovered ? "brightness(0.9) saturate(1.2)" : "brightness(0.6) saturate(0.7)",
              transition: "filter 0.4s, transform 0.4s",
              transform: hovered ? "scale(1.05)" : "scale(1)",
            }}
          />
          <div style={{ position: "absolute", inset: 0, background: hovered ? `linear-gradient(to top, rgba(7,7,15,0.8) 0%, transparent 50%)` : "rgba(7,7,15,0.4)", transition: "background 0.3s" }} />
        </div>
      )}

      <div style={{ padding: "18px 20px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
          <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: 15, color: "#f2f2ff", margin: 0, lineHeight: 1.4 }}>{project.title}</h3>
          <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#6a6a88", flexShrink: 0, marginLeft: 8 }}>{project.year}</span>
        </div>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6a6a88", lineHeight: 1.7, margin: "0 0 14px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{project.description}</p>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {(project.tags || []).slice(0, 4).map(tag => (
            <span key={tag} style={{ fontFamily: "'DM Mono', monospace", fontSize: 10, color: catColor, background: `${catColor}12`, border: `1px solid ${catColor}25`, borderRadius: 2, padding: "2px 7px", letterSpacing: 0.5 }}>{tag}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Portfolio() {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("Semua");
  const [loading, setLoading] = useState(true);
  const ref = useRef();

  useEffect(() => { getProjects().then(d => { setProjects(d); setLoading(false); }).catch(() => setLoading(false)); }, []);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { e.target.classList.add("visible"); obs.disconnect(); } }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const filtered = filter === "Semua" ? projects : projects.filter(p => p.category === filter);

  return (
    <div style={{ padding: "120px 0", background: "var(--surface)", position: "relative", overflow: "hidden" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 32px" }}>

        <div ref={ref} className="reveal" style={{ marginBottom: 64, position: "relative" }}>
          <div className="ghost-number" style={{ position: "absolute", left: -20, top: -60, zIndex: 0 }}>03</div>
          <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 24 }}>
            <div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#6a6a88", letterSpacing: 3, marginBottom: 12 }}>PORTFOLIO</div>
              <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(36px, 6vw, 72px)", fontWeight: 700, color: "#f2f2ff", margin: 0, letterSpacing: "-2px", lineHeight: 1 }}>
                Proyek<br />
                <span style={{ background: "linear-gradient(135deg,#00e5ff,#9b00ff,#ff0099,#ffb700,#00e5ff)", backgroundSize: "400% 400%", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", animation: "holo-shift 5s ease infinite" }}>Unggulan</span>
              </h2>
            </div>
            {/* Filters */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {FILTERS.map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  style={{
                    padding: "8px 18px", borderRadius: 2,
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 12, letterSpacing: 1,
                    border: "1px solid",
                    borderColor: filter === f ? "#00e5ff" : "rgba(255,255,255,0.1)",
                    background: filter === f ? "rgba(0,229,255,0.08)" : "transparent",
                    color: filter === f ? "#00e5ff" : "#6a6a88",
                    cursor: "none", transition: "all 0.2s",
                  }}>{f.toUpperCase()}</button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 80, color: "#6a6a88", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>MEMUAT PROYEK...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }} className="port-grid">
            {filtered.map((p, i) => <HoloCard key={p.id} project={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
